import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";

import { settingsGroups } from "@/components/settings-groups";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/configuracoes")({
  staticData: {
    title: "Configurações",
    subtitle: "Dados do estabelecimento, horários e integrações.",
  },
  component: ConfiguracoesLayout,
});

/**
 * A moldura de Configurações: trilha de grupos no desktop, drill-down no
 * celular.
 *
 * O caminho de volta mora aqui, e não em cada grupo, por dois motivos: são
 * oito páginas que precisariam repetir o mesmo link, e é o layout que sabe
 * se estamos no hub (onde não há pra onde voltar) ou dentro de um grupo.
 *
 * No desktop a trilha continua visível DENTRO do grupo: pular de "Pagamentos"
 * pra "Lembretes" é um clique, não voltar-e-entrar. No celular ela não cabe,
 * e o padrão é o que o iOS e o Android fazem — lista, e a tela do ajuste
 * ocupando a largura toda.
 */
function ConfiguracoesLayout() {
  const { pathname } = useLocation();
  const noHub = pathname === "/configuracoes" || pathname === "/configuracoes/";

  return (
    <div className="mx-auto grid w-full max-w-6xl gap-6 p-4 sm:p-6 lg:grid-cols-[13.5rem_minmax(0,1fr)] lg:gap-10">
      <nav aria-label="Grupos de configuração" className="hidden lg:block">
        {/* Fundo e borda de cartão, e não oito links soltos no branco da
            página: sem uma caixa em volta a trilha não lê como navegação —
            lê como uma lista de links empilhados perdida ao lado do
            conteúdo, sem dizer que aquilo ali é o índice desta tela. */}
        <ul className="sticky top-6 space-y-0.5 rounded-lg border bg-card p-2">
          {settingsGroups.map((group) => (
            <li key={group.to}>
              <Link
                to={group.to}
                activeProps={{ "data-active": true }}
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors",
                  "hover:bg-accent/60 hover:text-foreground",
                  // O ativo é o único item com peso e cor cheia — é o que
                  // responde "onde eu estou" de relance.
                  "data-[active=true]:bg-accent data-[active=true]:font-medium data-[active=true]:text-accent-foreground",
                )}
              >
                <group.icon className="h-4 w-4 shrink-0" />
                {group.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div className="min-w-0">
        {!noHub && (
          // Só no celular: no desktop a trilha ao lado já diz onde a pessoa
          // está e o voltar viraria um botão sem função.
          <Link
            to="/configuracoes"
            // `min-h-11`: este link é EXCLUSIVO do celular — é o único
            // caminho de volta ao hub — e mesmo assim tinha 28px de altura.
            className="-ml-1.5 mb-3 inline-flex min-h-11 items-center gap-1 rounded-md px-1.5 text-sm text-muted-foreground hover:text-foreground lg:hidden"
          >
            <ChevronLeft className="h-4 w-4" />
            Configurações
          </Link>
        )}
        <Outlet />
      </div>
    </div>
  );
}
